export const ADMIN_OAUTH_ERROR_STORAGE_KEY = "tempstars.admin.oauthError";

export interface AdminOAuthFailure {
  code: string;
  message: string;
  status: number;
}

const SAFE_MESSAGES: Record<string, string> = {
  INVALID_AUTH_TOKEN:
    "Your Google sign-in token was invalid or expired. Please try again.",
  INVALID_IDENTITY_TOKEN:
    "Your Google sign-in token was invalid or expired. Please try again.",
  ID_TOKEN_REQUIRED:
    "Google sign-in did not provide an identity token. Please try again.",
  EMAIL_DOMAIN_NOT_ALLOWED:
    "This Google Workspace account is not authorized for the TempStars Admin Panel.",
  ADMIN_PROVISIONING_FAILED:
    "We couldn't create your Admin access. Please contact support.",
  SESSION_CHECK_FAILED: "We couldn't verify your sign-in. Please try again.",
  ACCOUNT_DEACTIVATED: "This account has been deactivated.",
  ACCOUNT_BLOCKED:
    "This account is not authorized for the TempStars Admin Panel."
};

const isKnownSafeCode = (code: string): boolean =>
  Object.prototype.hasOwnProperty.call(SAFE_MESSAGES, code);

const messageFor = (code: string, status: number): string => {
  if (isKnownSafeCode(code)) {
    return SAFE_MESSAGES[code];
  }
  if (status === 403) {
    return "This Google Workspace account is not authorized for the TempStars Admin Panel.";
  }
  if (status >= 500) {
    return "Google sign-in is temporarily unavailable. Please try again.";
  }
  return "Google sign-in could not be completed. Please try again.";
};

export class AdminOAuthLoginError extends Error {
  code: string;
  status: number;

  constructor(failure: AdminOAuthFailure) {
    super(failure.message);
    this.name = "AdminOAuthLoginError";
    this.code = failure.code;
    this.status = failure.status;
    Object.setPrototypeOf(this, AdminOAuthLoginError.prototype);
  }
}

export const adminOAuthFailureFromResponse = (
  payload: any,
  responseStatus: number
): AdminOAuthFailure => {
  const nestedError =
    payload && payload.error && typeof payload.error === "object"
      ? payload.error
      : null;
  const rawCode =
    (nestedError && nestedError.code) || (payload && payload.code) || "";
  const code =
    typeof rawCode === "string" && isKnownSafeCode(rawCode)
      ? rawCode
      : "AUTH_PROCESSING_FAILED";
  const nestedStatus = nestedError && Number(nestedError.statusCode);
  const status = Number.isFinite(nestedStatus) ? nestedStatus : responseStatus;

  return {
    code,
    status,
    message: messageFor(code, status)
  };
};

export const normalizeAdminOAuthFailure = (
  error: unknown
): AdminOAuthFailure => {
  if (error instanceof AdminOAuthLoginError) {
    return {
      code: error.code,
      status: error.status,
      message: messageFor(error.code, error.status)
    };
  }

  return {
    code: "AUTH_PROCESSING_FAILED",
    status: 0,
    message: messageFor("AUTH_PROCESSING_FAILED", 0)
  };
};

const browserSessionStorage = (): Storage | undefined =>
  typeof window === "undefined" ? undefined : window.sessionStorage;

export const persistAdminOAuthFailure = (
  error: unknown,
  storage: Storage | undefined = browserSessionStorage()
): AdminOAuthFailure => {
  const failure = normalizeAdminOAuthFailure(error);
  if (storage) {
    try {
      storage.setItem(ADMIN_OAUTH_ERROR_STORAGE_KEY, JSON.stringify(failure));
    } catch {
      return failure;
    }
  }
  return failure;
};

export const consumeAdminOAuthFailure = (
  storage: Storage | undefined = browserSessionStorage()
): AdminOAuthFailure | null => {
  if (!storage) {
    return null;
  }

  try {
    const rawFailure = storage.getItem(ADMIN_OAUTH_ERROR_STORAGE_KEY);
    storage.removeItem(ADMIN_OAUTH_ERROR_STORAGE_KEY);
    if (!rawFailure) {
      return null;
    }
    const parsed = JSON.parse(rawFailure);
    const rawCode = typeof parsed.code === "string" ? parsed.code : "";
    const code = isKnownSafeCode(rawCode) ? rawCode : "AUTH_PROCESSING_FAILED";
    const status = Number.isFinite(Number(parsed.status))
      ? Number(parsed.status)
      : 0;
    return { code, status, message: messageFor(code, status) };
  } catch {
    return null;
  }
};

export const adminOAuthRedirectMessage = (redirectCode: unknown): string => {
  const code = typeof redirectCode === "string" ? redirectCode : "";
  if (code === "access_denied" || code === "oauth_failure") {
    return "Google sign-in was cancelled or could not be completed.";
  }
  if (code === "no_code" || code === "no_tokens") {
    return "Google sign-in did not return the required authentication data. Please try again.";
  }
  if (code === "token_exchange_failed") {
    return "Google sign-in could not exchange the authorization code. Please try again.";
  }
  if (code === "admin_token") {
    return "Google sign-in succeeded, but an Admin session could not be established. Please try again.";
  }
  return messageFor("AUTH_PROCESSING_FAILED", 0);
};
